import { isPlatformBrowser, NgStyle } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import type { Subscription } from 'rxjs';
import { trackByOptionProperty } from '../helpers/helpers';
import { IconColor, IconModule, IconSize } from '../icon';
import { textVariants } from '../tailwind-text';
import { FontColor, TextSize } from '../text';
import type { ToggleOption } from './toggle-buttons.types';
import { ToggleButtonsColor } from './toggle-buttons.types';

@Component({
  standalone: true,
  imports: [IconModule, NgStyle],
  selector: 'fl-toggle-buttons',
  template: `
    <div
      class="Container"
      role="listbox"
      [attr.data-color]="color"
    >
      @for (
        option of options;
        track trackByOptionProperty(index, option);
        let index = $index;
        let isLast = $last
      ) {
        <div
          #toggleOptions
          role="option"
          tabindex=""
          class="Option {{ isLast ? 'mr-none' : 'mr-xxxsmall' }}"
          [class.Selected]="control.value === option.option"
          [attr.aria-selected]="control.value === option.option"
          [attr.data-option-value]="option.option"
          [attr.data-color]="color"
          [attr.data-icons]="icons"
          [attr.data-full-width]="fullWidth"
          (click)="changeValue(option)"
        >
          @if (icons) {
            <fl-icon
              [name]="option.value"
              [size]="iconSize"
              [color]="
                control.value === option.option
                  ? color === ToggleButtonsColor.DARK
                    ? IconColor.LIGHT
                    : IconColor.FOREGROUND
                  : color === ToggleButtonsColor.DARK
                    ? IconColor.LIGHT
                    : IconColor.DARK
              "
            ></fl-icon>
          } @else {
            <div class="DarkText">
              <p
                [class]="
                  control.value === option.option
                    ? selectedTextCls
                    : unselectedTextCls
                "
              >
                {{ option.value }}
              </p>
            </div>
          }
        </div>
      }

      <div
        #buttonElement
        class="Button"
        [attr.data-color]="color"
        [ngStyle]="{
          'width.px': selectedOptionWidth,
          transform: buttonTransform,
        }"
      ></div>
    </div>
  `,
  styleUrls: ['./toggle-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleButtonsComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private time = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);

  IconColor = IconColor;
  ToggleButtonsColor = ToggleButtonsColor;
  trackByOptionProperty = trackByOptionProperty;

  @Input() color = ToggleButtonsColor.LIGHT;
  @Input() control: FormControl<string>;
  @Input() options: readonly ToggleOption[] = [];
  @Input() icons = false;
  @Input() iconSize = IconSize.MID;
  @Input() textSize = TextSize.XSMALL;

  @HostBinding('attr.data-full-width')
  @Input()
  fullWidth = false;

  private optionsSubscription?: Subscription;
  selectedOptionWidth = 0;
  buttonTransform = 'translateX(0)';

  selectedTextCls: string;
  unselectedTextCls: string;

  @ViewChild('buttonElement') buttonElementRef!: ElementRef<HTMLDivElement>;
  @ViewChildren('toggleOptions') optionElements!: QueryList<
    ElementRef<HTMLDivElement>
  >;

  private resizeObserver?: ResizeObserver;

  ngOnInit(): void {
    this.computeTextClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('textSize' in changes || 'color' in changes || 'control' in changes) {
      this.computeTextClasses();
    }
    if ('control' in changes) {
      this.optionsSubscription?.unsubscribe();
      this.optionsSubscription = this.control.valueChanges.subscribe(() =>
        this.updateButtonTransform(),
      );
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupResizeObserver();
    }
    this.time.setTimeout(() => this.updateButtonTransform());
  }

  ngOnDestroy(): void {
    this.optionsSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private getSelectedElement(): ElementRef<HTMLDivElement> | undefined {
    return this.optionElements.find(
      element =>
        element.nativeElement.getAttribute('data-option-value') ===
        this.control.value,
    );
  }

  private setupResizeObserver(): void {
    const selectedElement = this.getSelectedElement();

    if (selectedElement?.nativeElement.clientWidth === 0) {
      // Element is not yet visible or has no width
      this.resizeObserver = new ResizeObserver(() => {
        // Upon resize, update the width and perform any necessary operations
        this.time.setTimeout(() => this.updateButtonTransform());
      });

      this.resizeObserver.observe(selectedElement?.nativeElement);
    }
  }

  updateButtonTransform(): void {
    const buttonElement = this.buttonElementRef.nativeElement;
    const selectedElement = this.getSelectedElement();

    if (selectedElement) {
      this.selectedOptionWidth = selectedElement.nativeElement.clientWidth;
      const transformOffset =
        selectedElement.nativeElement.offsetLeft - buttonElement.offsetLeft;
      this.buttonTransform = `translateX(${transformOffset}px)`;

      this.changeDetectorRef.markForCheck();
    }
  }

  changeValue(option: ToggleOption): void {
    this.control.setValue(option.option);
  }

  private computeTextClasses(): void {
    const selectedColor =
      this.color === ToggleButtonsColor.DARK
        ? FontColor.LIGHT
        : FontColor.FOREGROUND;
    const unselectedColor =
      this.color === ToggleButtonsColor.DARK
        ? FontColor.LIGHT
        : FontColor.INHERIT;

    this.selectedTextCls = textVariants({
      color: selectedColor,
      size: this.textSize,
    });
    this.unselectedTextCls = textVariants({
      color: unselectedColor,
      size: this.textSize,
    });
  }
}
